import React, { ChangeEvent, FC, ReactElement } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import get from 'lodash/get';
import set from 'lodash/set';
// Components
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import Text from '../text';
import ModalButton, { ButtonType } from './templates/modalButton';
import ModalButtonContainer from './templates/modalButtonContainer';
import DatePicker from '../datePicker/datePicker';
import InfoBoxTex from '../infoBoxText';
// Resources
import { stringsRes } from '../../resources/strings';
import { PropsFromModal } from './createModalHOC';
import SvgIcon from '../icons/SvgIcon';
import { IconCommon } from '../../resources/images';
import useGlobalStyles from '../hook/useGlobalStyles';

interface RecurrenceProps extends PropsFromModal {
    mode?: 'add' | 'edit' | 'info';
    formData?: any;
    description?: string;
    labelCancel?: string;
    typeCancel?: ButtonType;
    labelConfirm?: string;
    typeConfirm?: ButtonType;
    onConfirmAction?: (data: any) => void;
    onCancelAction?: () => void;
}

const Recurrence: FC<RecurrenceProps> = (props): ReactElement => {
    const {
        onRequestClose,
        mode = 'add',
        formData = {
            start_date: moment(),
            end_date: moment().add(1, 'week'),
        },
        description,
        labelCancel,
        typeCancel,
        labelConfirm,
        typeConfirm,
        onConfirmAction,
        onCancelAction,
    } = props;
    const classes = useStyle();
    const globalClasses = useGlobalStyles();

    const [data, setData] = React.useState<any>(formData);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // console.log(data, e.target.name, e.target.value);
        const newState = set({ ...data }, e.target.name, e.target.value);
        setData(newState);
    };

    const onClickConfirm = (): void => {
        onConfirmAction?.(data);
        onRequestClose();
    };

    const onClickCancel = (): void => {
        onCancelAction?.();
        onRequestClose();
    };

    const isEdit = mode === 'edit';

    return (
        <div className={classes.recurrenceContainer}>
            {description && (
                <div className={globalClasses.flexCol}>
                    <Text.Description16 text={description} />
                </div>
            )}

            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="frFR">
                <div className={classes.formContent}>
                    <DatePicker
                        label={stringsRes.common.startDateLabel}
                        noError
                        limitDate
                        iconElement={<SvgIcon icon={IconCommon.Calendar} />}
                        selectedDate={moment(get(data, 'start_date')) || null}
                        onChangeDateTime={(date: any) =>
                            onChange({
                                target: { value: date, name: 'start_date' },
                            } as unknown as ChangeEvent<HTMLInputElement>)
                        }
                        fullWidth
                    />
                    <Text.Body1 text="—" />
                    <DatePicker
                        label={stringsRes.common.endDateLabel}
                        noError
                        limitDate
                        iconElement={<SvgIcon icon={IconCommon.Calendar} />}
                        selectedDate={moment(get(data, 'end_date')) || null}
                        onChangeDateTime={(date: any) =>
                            onChange({
                                target: { value: date, name: 'end_date' },
                            } as unknown as ChangeEvent<HTMLInputElement>)
                        }
                        fullWidth
                    />
                </div>
            </LocalizationProvider>

            <InfoBoxTex description={stringsRes.recurrence.hint} className={classes.recurrenceInfoBox} />

            <ModalButtonContainer>
                <ModalButton
                    type={typeCancel ?? ButtonType.CANCEL}
                    onClick={onClickCancel}
                    label={labelCancel ?? stringsRes.common.cancel}
                />
                <ModalButton
                    type={typeConfirm ?? ButtonType.CONFIRM}
                    onClick={onClickConfirm}
                    label={labelConfirm ?? (isEdit ? stringsRes.common.edit : stringsRes.common.confirm)}
                />
            </ModalButtonContainer>
        </div>
    );
};

const useStyle = makeStyles(() =>
    createStyles({
        recurrenceContainer: {
            '& .MuiButtonBase-root': {
                maxWidth: 'unset',
            },
        },
        formContent: {
            display: 'flex',
            flexDirection: 'row',
            gap: 16,
            marginTop: 16,
            marginBottom: 16,
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        recurrenceInfoBox: {
            marginTop: 8,
            flexDirection: 'row',
            columnGap: 12,
        },
    }),
);

export default Recurrence;
